import { createBrowserRouter, RouterProvider, Navigate, Outlet, useParams } from "react-router-dom";
import { ROUTES } from "@/config/constants";
import { PrivateRoute } from "./PrivateRoute";
import { LoginPage } from "@/pages/Login";
import { HomePage } from "@/pages/Home";
import { SuprimentosPage } from "@/pages/Suprimentos";
import { SuprimentosKanbanPage } from "@/pages/Suprimentos/SuprimentosKanbanPage";
import { FinanceiroPage } from "@/pages/Financeiro";
import { ComercialPage } from "@/pages/Comercial";
import { OrcamentoPage } from "@/pages/Orcamento";
import { GerencialPage } from "@/pages/Gerencial";
import { ChaoDeObraListPage } from "@/pages/Gerencial/ChaoDeObra/ChaoDeObraListPage";
import { ChaoDeObraUnitPage } from "@/pages/Gerencial/ChaoDeObra/ChaoDeObraUnitPage";
import { ChaoDeObraReportPage } from "@/pages/Gerencial/ChaoDeObra/ChaoDeObraReportPage";
import { SolicitacaoPagamentoPage } from "@/pages/SolicitacaoPagamento";
import { TarefasPage } from "@/pages/Tarefas";
import { LocacoesPage } from "@/pages/Locacoes";
import { ManutencaoPage } from "@/pages/Manutencao";
import { FolhaPagamentosPage } from "@/pages/FolhaPagamentos";
import { AppLayout } from "@/layouts/AppLayout";
import { SuprimentosKanbanShell } from "@/layouts/SuprimentosKanbanShell";
import { ModulesProvider } from "@/context/ModulesContext";
import { ModuleAccess } from "./ModuleAccess";
import { AdminRoute } from "./AdminRoute";
import { AdminAcessoPage } from "@/pages/AdminAcesso";
import { WhatsappPage } from "@/pages/Whatsapp";
import { RouteErrorFallback } from "./RouteErrorFallback";
import { OperadorRoute } from "./OperadorRoute";
import { OperadorLayout } from "@/layouts/OperadorLayout";
import { OperadorHomePage } from "@/pages/Operador";
import { OperadorHorimetroPage } from "@/pages/Operador/HorimetroPage";
import { OperadorManutencaoPage } from "@/pages/Operador/ManutencaoPage";
import { ChamadosTiPage } from "@/pages/ChamadosTi/ChamadosTiPage";
import { MarketingPage } from "@/pages/Marketing";
import { GenteGestaoLayout } from "@/pages/GenteGestao/GenteGestaoLayout";
import { GenteGestaoMenuPage } from "@/pages/GenteGestao/GenteGestaoMenuPage";
import { GenteGestaoVisaoPage } from "@/pages/GenteGestao/GenteGestaoVisaoPage";
import { GenteGestaoColaboradoresPage } from "@/pages/GenteGestao/GenteGestaoColaboradoresPage";
import { GenteGestaoNpsListPage } from "@/pages/GenteGestao/GenteGestaoNpsListPage";
import { GenteGestaoNpsDetalhePage } from "@/pages/GenteGestao/GenteGestaoNpsDetalhePage";
import { GenteGestaoLiderancaListPage } from "@/pages/GenteGestao/GenteGestaoLiderancaListPage";
import { GenteGestaoLiderancaCompetenciasPage } from "@/pages/GenteGestao/GenteGestaoLiderancaCompetenciasPage";
import { GenteGestaoLiderancaDetalhePage } from "@/pages/GenteGestao/GenteGestaoLiderancaDetalhePage";
import { PesquisaNpsTokenPage } from "@/pages/PesquisaNps/PesquisaNpsTokenPage";
import { PesquisaNpsSlugPage } from "@/pages/PesquisaNps/PesquisaNpsSlugPage";
import { PesquisaLiderancaTokenPage } from "@/pages/PesquisaLideranca/PesquisaLiderancaTokenPage";
import { EscutaPublicaPage } from "@/pages/Escuta/EscutaPublicaPage";
import { EscutaProtocoloPage } from "@/pages/Escuta/EscutaProtocoloPage";
import { GenteGestaoEscutaPage } from "@/pages/GenteGestao/GenteGestaoEscutaPage";
import { GenteGestaoDesligamentoPage } from "@/pages/GenteGestao/GenteGestaoDesligamentoPage";
import { DesligamentoPublicaPage } from "@/pages/PesquisaDesligamento/DesligamentoPublicaPage";

function LegacyChaoDeObraUnitRedirect() {
  const { unitId } = useParams<{ unitId: string }>();
  const id = unitId ? decodeURIComponent(unitId) : "";
  return <Navigate to={`${ROUTES.CHAO_DE_OBRA}/unidade/${encodeURIComponent(id)}`} replace />;
}

const router = createBrowserRouter([
  {
    path: ROUTES.LOGIN,
    element: <LoginPage />,
  },
  {
    path: "/pesquisa/nps/r/:token",
    element: <PesquisaNpsTokenPage />,
  },
  {
    path: "/pesquisa/nps/:slug",
    element: <PesquisaNpsSlugPage />,
  },
  {
    path: "/pesquisa/lideranca/r/:token",
    element: <PesquisaLiderancaTokenPage />,
  },
  {
    path: "/pesquisa/desligamento/r/:token",
    element: <DesligamentoPublicaPage />,
  },
  {
    path: "/escuta",
    element: <EscutaPublicaPage />,
  },
  {
    path: "/escuta/p/:protocolo",
    element: <EscutaProtocoloPage />,
  },

  {
    element: <PrivateRoute />,
    errorElement: <RouteErrorFallback />,
    children: [
      {
        path: ROUTES.HOME,
        element: <HomePage />,
      },
      {
        element: <OperadorRoute />,
        children: [
          {
            element: <OperadorLayout />,
            children: [
              { path: ROUTES.OPERADOR, element: <OperadorHomePage /> },
              { path: ROUTES.OPERADOR_HORIMETRO, element: <OperadorHorimetroPage /> },
              { path: ROUTES.OPERADOR_MANUTENCAO, element: <OperadorManutencaoPage /> },
            ],
          },
        ],
      },
      {
        path: ROUTES.ADMIN_ACESSO,
        element: (
          <AdminRoute>
            <AdminAcessoPage />
          </AdminRoute>
        ),
      },
      {
        path: ROUTES.WHATSAPP,
        element: (
          <AdminRoute>
            <WhatsappPage />
          </AdminRoute>
        ),
      },
      {
        element: (
          <ModulesProvider>
            <Outlet />
          </ModulesProvider>
        ),
        children: [
          {
            element: <AppLayout />,
            children: [
              {
                path: ROUTES.DASHBOARD,
                element: <Navigate to={ROUTES.GERENCIAL} replace />,
              },
              {
                path: ROUTES.SUPRIMENTOS,
                element: (
                  <ModuleAccess module="suprimentos">
                    <SuprimentosPage />
                  </ModuleAccess>
                ),
              },
              {
                path: ROUTES.FINANCEIRO,
                element: (
                  <ModuleAccess module="financeiro">
                    <FinanceiroPage />
                  </ModuleAccess>
                ),
              },
              {
                path: ROUTES.COMERCIAL,
                element: (
                  <ModuleAccess module="comercial">
                    <ComercialPage />
                  </ModuleAccess>
                ),
              },
              {
                path: ROUTES.ORCAMENTO,
                element: (
                  <ModuleAccess module="orcamentos">
                    <OrcamentoPage />
                  </ModuleAccess>
                ),
              },
              {
                path: ROUTES.GERENCIAL,
                element: (
                  <ModuleAccess module="gerencial">
                    <GerencialPage />
                  </ModuleAccess>
                ),
              },
              {
                path: ROUTES.CHAO_DE_OBRA,
                element: (
                  <ModuleAccess module="chaoDeObra">
                    <ChaoDeObraListPage />
                  </ModuleAccess>
                ),
              },
              {
                path: `${ROUTES.CHAO_DE_OBRA}/unidade/:unitId`,
                element: (
                  <ModuleAccess module="chaoDeObra">
                    <ChaoDeObraUnitPage />
                  </ModuleAccess>
                ),
              },
              {
                path: `${ROUTES.CHAO_DE_OBRA}/unidade/:unitId/relatorio/:reportId`,
                element: (
                  <ModuleAccess module="chaoDeObra">
                    <ChaoDeObraReportPage />
                  </ModuleAccess>
                ),
              },
              {
                path: ROUTES.SOLICITACAO_PAGAMENTO,
                element: (
                  <ModuleAccess solicitacaoPagamentoModule>
                    <SolicitacaoPagamentoPage />
                  </ModuleAccess>
                ),
              },
              {
                path: ROUTES.TAREFAS,
                element: (
                  <ModuleAccess module="tarefas">
                    <TarefasPage />
                  </ModuleAccess>
                ),
              },
              {
                path: ROUTES.LOCACOES,
                element: (
                  <ModuleAccess locacoesModule>
                    <LocacoesPage />
                  </ModuleAccess>
                ),
              },
              {
                path: ROUTES.MANUTENCAO,
                element: (
                  <ModuleAccess module="manutencao">
                    <ManutencaoPage />
                  </ModuleAccess>
                ),
              },
              {
                path: ROUTES.FOLHA_PAGAMENTOS,
                element: (
                  <ModuleAccess module="folhaPagamentos">
                    <FolhaPagamentosPage />
                  </ModuleAccess>
                ),
              },
              {
                path: ROUTES.CHAMADOS_TI,
                element: <ChamadosTiPage />,
              },
              {
                path: ROUTES.MARKETING,
                element: (
                  <ModuleAccess module="marketing">
                    <MarketingPage />
                  </ModuleAccess>
                ),
              },
              {
                element: (
                  <ModuleAccess module="genteGestao">
                    <GenteGestaoLayout />
                  </ModuleAccess>
                ),
                children: [
                  { path: ROUTES.GENTE_GESTAO, element: <GenteGestaoMenuPage /> },
                  { path: ROUTES.GENTE_GESTAO_VISAO, element: <GenteGestaoVisaoPage /> },
                  { path: ROUTES.GENTE_GESTAO_COLABORADORES, element: <GenteGestaoColaboradoresPage /> },
                  { path: ROUTES.GENTE_GESTAO_NPS, element: <GenteGestaoNpsListPage /> },
                  { path: `${ROUTES.GENTE_GESTAO_NPS}/:id`, element: <GenteGestaoNpsDetalhePage /> },
                  { path: ROUTES.GENTE_GESTAO_LIDERANCA, element: <GenteGestaoLiderancaListPage /> },
                  {
                    path: ROUTES.GENTE_GESTAO_LIDERANCA_COMPETENCIAS,
                    element: <GenteGestaoLiderancaCompetenciasPage />,
                  },
                  { path: `${ROUTES.GENTE_GESTAO_LIDERANCA}/:id`, element: <GenteGestaoLiderancaDetalhePage /> },
                  { path: ROUTES.GENTE_GESTAO_ESCUTA, element: <GenteGestaoEscutaPage /> },
                  { path: ROUTES.GENTE_GESTAO_DESLIGAMENTO, element: <GenteGestaoDesligamentoPage /> },
                ],
              },
              {
                path: "/gerencial/chao-de-obra",
                element: <Navigate to={ROUTES.CHAO_DE_OBRA} replace />,
              },
              {
                path: "/gerencial/chao-de-obra/unidade/:unitId",
                element: <LegacyChaoDeObraUnitRedirect />,
              },
            ],
          },
          {
            element: <SuprimentosKanbanShell />,
            children: [
              {
                path: ROUTES.SUPRIMENTOS_KANBAN,
                element: (
                  <ModuleAccess module="kanban">
                    <SuprimentosKanbanPage />
                  </ModuleAccess>
                ),
              },
            ],
          },
        ],
      },
    ],
  },

  {
    path: "*",
    element: <Navigate to={ROUTES.HOME} replace />,
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
